import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TrailDocumentFmea } from '../../dto/trail-document-fmea';
import { TrailDocumentFmeaService } from '../../service/trail-document-fmea.service';
import { Trail } from '../../dto/trail';
import { TrailDocumentFmeaDetailComponent } from '../trail-document-fmea-detail/trail-document-fmea-detail.component';

@Component({
  selector: 'dpi-trail-document-fmea',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule,
    MatTooltipModule, NgIf],
  templateUrl: './trail-document-fmea.component.html',
  styleUrl: './trail-document-fmea.component.scss'
})
export class TrailDocumentFmeaComponent implements OnInit, OnDestroy {
  trail: Trail;
  displayedColumns: string[] = ['fmea_report', 'last_date', 'fmea_findings_closed_text', 'last_dp_anual_trails_date', 'trail_findings_closed_text', 'remark'];
  dataSource: TrailDocumentFmea[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private trailDocumentFmeaService: TrailDocumentFmeaService) {
    this.trail = this.notifyService.getSelectedTrail();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.trail = model;
        this.reload(model);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reload(selectedTrail: Trail) {
    this.trailDocumentFmeaService.getAllSetupOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
          for (var obj of this.dataSource) {
            if (obj.fmea_findings_closed) {
              obj.fmea_findings_closed_text = 'Yes';
            } else {
              obj.fmea_findings_closed_text = 'No';
            }
            if (obj.trail_findings_closed) {
              obj.trail_findings_closed_text = 'Yes';
            } else {
              obj.trail_findings_closed_text = 'No';
            }
          }
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  selectTrailDocumentFmea(selected: TrailDocumentFmea) {
    if (!this.isReadonly) {
      var objCopy: TrailDocumentFmea = { ...selected };
      const dialogRef = this._dialog.open(TrailDocumentFmeaDetailComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.trailDocuFMEA);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.trailDocuFMEA.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  create() {
    this.trailDocumentFmeaService.createDocumentFmea(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectTrailDocumentFmea(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: TrailDocumentFmea) {
    let list: TrailDocumentFmea[] = [];
    for (var item of this.dataSource) {
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  update(model: TrailDocumentFmea) {
    this.trailDocumentFmeaService.updateDocumentFmea(model)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.replaceModelInDataSource(res);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: TrailDocumentFmea) {
    let list: TrailDocumentFmea[] = [];
    for (var item of this.dataSource) {
      if (item.id === model.id) {
        if (model.fmea_findings_closed) {
          model.fmea_findings_closed_text = 'Yes';
        } else {
          model.fmea_findings_closed_text = 'No';
        }
        if (model.trail_findings_closed) {
          model.trail_findings_closed_text = 'Yes';
        } else {
          model.trail_findings_closed_text = 'No';
        }
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  delete(modelId: number) {
    this.trailDocumentFmeaService.deleteDocumentFmea(modelId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(modelId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(modelId: number) {
    let list: TrailDocumentFmea[] = [];
    for (var item of this.dataSource) {
      if (item.id === modelId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }


}
